/**
 * ACSM User Roles
 *
 * Roles stored in `user_roles` table in Supabase.
 * Fallback: if a user has no row, they're treated as 'guest'.
 *
 * Role capabilities:
 *   admin              — full access: all sections, bulk upload, edit agents/SAs, reports, download,
 *                        infractions, scoring, enforcement user management, run scoring,
 *                        approve/modify all compliance resolutions
 *   enforcement        — log infractions, add notes; logged-in user auto-fills "Logged By"
 *   compliance_officer — open infraction cases, log PMO resolutions, mark penalties paid
 *   acsm_officer       — view all, upload wallet funding, run scoring
 *   guest              — view-only; can view dashboards and export reports
 */

import { useEffect, useState } from 'react'
import { supabase } from './supabase'

export const ROLES = {
  ADMIN:              'admin',
  ENFORCEMENT:        'enforcement',
  COMPLIANCE_OFFICER: 'compliance_officer',
  ACSM_OFFICER:       'acsm_officer',
  GUEST:              'guest',
}

export const ROLE_LABELS = {
  admin:              'Admin',
  enforcement:        'Enforcement Officer',
  compliance_officer: 'Compliance Officer',
  acsm_officer:       'ACSM Officer',
  guest:              'Guest',
}

// ── Permission helpers ──────────────────────────────────────────────────────

export function can(role, action) {
  const isCO    = role === ROLES.COMPLIANCE_OFFICER
  const isAdmin = role === ROLES.ADMIN
  const isEnf   = role === ROLES.ENFORCEMENT

  switch (action) {

    // Section visibility
    case 'view_dashboard':        return true
    case 'view_agents':           return true
    case 'view_scoring':          return true
    case 'view_target_board':     return true
    case 'view_infractions':      return true
    case 'view_reports':          return true
    case 'view_settings':         return true
    case 'view_enforcement':      return true
    case 'view_compliance':       return true  // all roles can see compliance review page

    // Editing / uploads
    case 'edit_agents':           return isAdmin
    case 'bulk_upload':           return isAdmin
    case 'upload_wallet':         return isAdmin || role === ROLES.ACSM_OFFICER
    case 'run_scoring':           return isAdmin || role === ROLES.ACSM_OFFICER
    case 'edit_settings':         return isAdmin
    case 'edit_target_settings':  return isAdmin
    case 'view_target_settings':  return true
    case 'change_tier_security':  return isAdmin

    // Infractions (stage 1 — Compliance Officer logs; Enforcement is view-only)
    case 'log_infractions':       return isAdmin || isCO
    case 'add_infraction_notes':  return isAdmin || isCO

    // Compliance resolution (stage 2 & 3 — Compliance Officer)
    case 'open_infraction_case':  return isAdmin || isCO
    case 'log_co_resolution':     return isAdmin || isCO
    case 'mark_penalty_paid':     return isAdmin || isCO

    // Admin approval (stage 4 — Admin only)
    case 'approve_resolution':    return isAdmin
    case 'modify_resolution':     return isAdmin

    // Enforcement cases
    case 'edit_enforcement':      return isAdmin || isEnf
    case 'close_enforcement_case':return isAdmin

    // Reports / downloads
    case 'export_reports':        return true
    case 'download_agents':       return isAdmin
    case 'download_scores':       return isAdmin || role === ROLES.ACSM_OFFICER

    // User management
    case 'manage_users':          return isAdmin

    default: return false
  }
}

// ── React hook ──────────────────────────────────────────────────────────────

export function useRole(session) {
  const [role,    setRole]    = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!session?.user) { setRole(ROLES.GUEST); setLoading(false); return }
    supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', session.user.id)
      .maybeSingle()
      .then(({ data }) => {
        setRole(data?.role || ROLES.GUEST)
        setLoading(false)
      })
  }, [session])

  return { role, loading, can: (action) => role ? can(role, action) : false }
}
